import * as XLSX from "xlsx";
import type { DaySchedule } from "@/components/agents/BusinessHoursSelector";
import type { AddressItem } from "@/components/agents/sections/AddressManager";
import type { HandoffRule } from "@/components/agents/sections/HandoffRuleInput";

export const AGENT_CONFIG_EXPORT_VERSION = 2;

export type AgentConfigFormValues = {
  name: string;
  display_name?: string;
  greeting_message?: string;
  role: "vendas" | "suporte" | "sdr" | "agendamentos";
  tone?: string;
  language: string;
  timezone: string;
  is_active: boolean;
  restrict_to_business_hours: boolean;
  google_calendar_enabled: boolean;
  google_calendar_sync_calendar_id?: string | null;
  appointment_duration: number;
  max_appointment_options: number;
  scheduling_start_offset_days: number;
  scheduling_search_days: number;
  allow_overlapping_appointments: boolean;
  address_enabled: boolean;
  phone_numbers: string[];
  phone_numbers_enabled: boolean;
  social_media?: {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
    twitter?: string;
    whatsapp?: string;
    email?: string;
    website?: string;
  };
  social_media_enabled: boolean;
  comment_website_link_placement: "public" | "dm_only";
  comment_product_link_placement: "public" | "dm_only";
  comment_reply_mode: "ai_decides" | "always_both" | "public_only";
  establishment_name?: string;
  can_schedule: boolean;
  can_reschedule: boolean;
  can_cancel: boolean;
  cnpj?: string;
  other_social_media?: string;
  payment_methods: string[];
  payment_installments?: string;
  show_prices_to_customer: boolean;
  price_disclosure_mode: "free" | "value_first" | "never";
  price_disclosure_condition?: string | null;
  delivery_info?: string;
  delivery_enabled: boolean;
  about_company?: string;
  target_audience?: string;
  value_proposition?: string;
  agent_goal?: string;
  desired_next_step?: string;
  service_area?: string;
  limitations?: string;
  policies_summary?: string;
  required_customer_data?: string;
  llm_temperature: number;
};

export type AgentConfigSnapshot = {
  form: AgentConfigFormValues;
  qualificationQuestions: string[];
  businessRulesList: string[];
  agentInstructions: string[];
  addresses: AddressItem[];
  faqItems: { question: string; answer: string }[];
  businessCategory: string;
  businessTags: string[];
  humanHandoffEnabled: boolean;
  humanHandoffRules: string[];
  humanHandoffRulesV2: HandoffRule[];
  agentEscalationRulesV2?: {
    condition: string;
    target_agent_id: string;
    label?: string;
    return_mode?: "auto_after_scheduling" | "auto_after_task" | "explicit";
    task_type?: "scheduling" | "technical" | "sales" | "general";
  }[];
  catalogEnabled: boolean;
  catalogFileUrl: string | null;
  catalogStorageProvider: string | null;
  catalogBucket: string | null;
  catalogObjectKey: string | null;
  catalogMimeType: string | null;
  catalogFileSize: number | null;
  catalogPublicUrl: string | null;
  catalogFileName: string | null;
  catalogTriggerPhrases: string[];
  selectedProductIds: string[];
  businessHours: DaySchedule[];
  followUpEnabled: boolean;
  followUpInterval: number;
  followUpMaxAttempts: number;
};

/** Linha amigável para planilha (sem coluna técnica obrigatória). */
export type AgentConfigRow = {
  /** Aba da tela: Perfil, Negócio ou Comportamento */
  secao: string;
  /** Título da seção no prompt da IA */
  blocoPrompt?: string;
  campo: string;
  valor: string;
  chave?: string;
};

const ABA_PERFIL = "Perfil";
const ABA_NEGOCIO = "Negócio";
const ABA_COMPORTAMENTO = "Comportamento";

type ListFormat = "numbered" | "bullets" | "faq" | "plain";

const PROMPT_BLOCK_HINTS: Partial<Record<string, string>> = {
  "REGRAS E POLÍTICAS DO NEGÓCIO — OBRIGATÓRIAS":
    "No prompt vira lista numerada vinculante (1. 2. 3.). Uma regra por linha aqui ou já numerada.",
  "PERGUNTAS DE QUALIFICAÇÃO":
    "No prompt o agente faz uma pergunta por vez, em lista numerada.",
  "INSTRUÇÕES DO AGENTE (PRIORIDADE MÁXIMA)":
    "No prompt entram como lista numerada com prioridade máxima.",
  "PERGUNTAS FREQUENTES (FAQ) — RESPOSTAS OFICIAIS":
    "Use P: pergunta e R: resposta (como no prompt).",
  "TRANSFERÊNCIA PARA ATENDIMENTO HUMANO":
    "No prompt cada situação vira item numerado para a tool de escalação.",
};

const ROLE_LABELS: Record<string, string> = {
  vendas: "Vendas",
  suporte: "Suporte",
  sdr: "Prospecção (SDR)",
  agendamentos: "Agendamentos",
};

const PRICE_MODE_LABELS: Record<string, string> = {
  free: "Pode informar preços livremente",
  value_first: "Só depois de entender a necessidade",
  never: "Não informar preços",
};

const SOCIAL_LABELS: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "Twitter / X",
  whatsapp: "WhatsApp",
  email: "E-mail",
  website: "Site",
};

const VALID_ROLES = new Set(["vendas", "suporte", "sdr", "agendamentos"]);
const VALID_PRICE_MODES = new Set(["free", "value_first", "never"]);

const INSTRUCTION_ROWS: AgentConfigRow[] = [
  {
    secao: "Como usar",
    campo: "Preencha só as linhas que quiser mudar",
    valor: "Deixe em branco o que não for alterar. Listas: uma linha por item. Sim/não para ligar e desligar opções.",
  },
  {
    secao: "Como usar",
    campo: "Função do agente",
    valor: "Use: Vendas, Suporte, Prospecção (SDR) ou Agendamentos",
  },
];

function normalizeHeader(h: string): string {
  return h
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

function boolToStr(v: boolean) {
  return v ? "sim" : "não";
}

function strToBool(v: string, fallback = false): boolean {
  const n = v.trim().toLowerCase();
  if (["sim", "true", "1", "yes", "s", "ativo", "ligado"].includes(n)) return true;
  if (["não", "nao", "false", "0", "no", "n", "inativo", "desligado"].includes(n)) return false;
  return fallback;
}

function parseJson<T>(raw: string, fallback: T): T {
  const t = raw.trim();
  if (!t) return fallback;
  if (!t.startsWith("[") && !t.startsWith("{")) return fallback;
  try {
    return JSON.parse(t) as T;
  } catch {
    return fallback;
  }
}

function formatStringList(items: string[]): string {
  return items.map((s) => s.trim()).filter(Boolean).join("\n");
}

function stripListMarker(line: string): string {
  return line
    .replace(/^\d+[\.\)]\s+/, "")
    .replace(/^[-*•]\s+/, "")
    .trim();
}

function parseStringList(raw: string): string[] {
  const t = raw.trim();
  if (!t) return [];
  const asJson = parseJson<string[]>(t, []);
  if (Array.isArray(asJson) && t.startsWith("[")) {
    return asJson.map(String).filter(Boolean);
  }
  return t
    .split(/\n|;/)
    .map((s) => stripListMarker(s.trim()))
    .filter(Boolean);
}

function formatValueForCopy(valor: string, listFormat?: ListFormat): string {
  const t = valor.trim();
  if (!t) return "(vazio)";

  if (listFormat === "numbered") {
    const items = parseStringList(t);
    if (items.length === 0) return "(vazio)";
    return items.map((item, i) => `${i + 1}. ${item}`).join("\n");
  }

  if (listFormat === "bullets") {
    const items = parseStringList(t);
    if (items.length === 0) return "(vazio)";
    return items.map((item) => `- ${item}`).join("\n");
  }

  if (listFormat === "faq") {
    return t;
  }

  return t;
}

function formatSocialMedia(sm: AgentConfigFormValues["social_media"]): string {
  if (!sm) return "";
  return Object.entries(sm)
    .filter(([, v]) => String(v ?? "").trim())
    .map(([k, v]) => `${SOCIAL_LABELS[k] ?? k}: ${v}`)
    .join("\n");
}

function parseSocialMedia(raw: string, current: AgentConfigFormValues["social_media"]): AgentConfigFormValues["social_media"] {
  const base = { ...(current ?? {}) };
  const lines = parseStringList(raw);
  const keyByLabel = new Map(
    Object.entries(SOCIAL_LABELS).map(([k, label]) => [normalizeHeader(label), k]),
  );
  for (const line of lines) {
    const sep = line.indexOf(":");
    if (sep < 0) continue;
    const left = normalizeHeader(line.slice(0, sep));
    const value = line.slice(sep + 1).trim();
    const key = keyByLabel.get(left) ?? (VALID_SOCIAL_KEYS.has(left) ? left : null);
    if (key && key in SOCIAL_LABELS) {
      (base as Record<string, string>)[key] = value;
    }
  }
  return base;
}

const VALID_SOCIAL_KEYS = new Set(Object.keys(SOCIAL_LABELS));

function formatRole(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

function parseRole(raw: string, fallback: AgentConfigFormValues["role"]): AgentConfigFormValues["role"] {
  const n = normalizeHeader(raw);
  if (VALID_ROLES.has(n)) return n as AgentConfigFormValues["role"];
  for (const [slug, label] of Object.entries(ROLE_LABELS)) {
    if (normalizeHeader(label) === n || normalizeHeader(slug) === n) return slug as AgentConfigFormValues["role"];
  }
  if (n.includes("venda")) return "vendas";
  if (n.includes("suporte")) return "suporte";
  if (n.includes("sdr") || n.includes("prospec")) return "sdr";
  if (n.includes("agenda")) return "agendamentos";
  return fallback;
}

function formatPriceMode(mode: string): string {
  return PRICE_MODE_LABELS[mode] ?? mode;
}

function parsePriceMode(raw: string, fallback: AgentConfigFormValues["price_disclosure_mode"]) {
  const n = normalizeHeader(raw);
  for (const [slug, label] of Object.entries(PRICE_MODE_LABELS)) {
    if (normalizeHeader(label) === n || slug === n) return slug as AgentConfigFormValues["price_disclosure_mode"];
  }
  if (n.includes("nunca")) return "never";
  if (n.includes("depois") || n.includes("entender")) return "value_first";
  if (n.includes("livre")) return "free";
  return VALID_PRICE_MODES.has(raw.trim()) ? (raw.trim() as AgentConfigFormValues["price_disclosure_mode"]) : fallback;
}

function formatLinkPlacement(value: string): string {
  return value === "dm_only" ? "Somente na DM" : "No comentário público";
}

function formatReplyMode(value: string): string {
  if (value === "always_both") return "Sempre comentário e DM";
  if (value === "public_only") return "Só comentário público";
  return "A IA decide";
}

function parseReplyMode(raw: string, fallback: "ai_decides" | "always_both" | "public_only") {
  const n = normalizeHeader(raw);
  if (n === "always_both" || n.includes("sempre")) return "always_both";
  if (n === "public_only" || n.includes("so comentario") || n.includes("somente comentario")) return "public_only";
  if (n === "ai_decides" || n.includes("decide")) return "ai_decides";
  return fallback;
}

function parseLinkPlacement(raw: string, fallback: "public" | "dm_only"): "public" | "dm_only" {
  const n = normalizeHeader(raw);
  if (n === "dm_only" || n.includes("somente") || n.includes("direct") || n.includes(" dm")) return "dm_only";
  if (n === "public" || n.includes("publico") || n.includes("public")) return "public";
  return fallback;
}

function parseHandoffRulesV2(raw: string): HandoffRule[] {
  const json = parseJson<HandoffRule[]>(raw, []);
  if (Array.isArray(json) && raw.trim().startsWith("[")) return json;
  return parseStringList(raw).map((condition) => ({
    condition,
    target_funnel_id: null,
    target_stage_slug: "atendimento_humano",
  }));
}

function parseFaqItems(raw: string): { question: string; answer: string }[] {
  const json = parseJson<{ question: string; answer: string }[]>(raw, []);
  if (Array.isArray(json) && raw.trim().startsWith("[")) return json;
  const items: { question: string; answer: string }[] = [];
  let q = "";
  let a = "";
  for (const line of raw.split(/\n/)) {
    const t = line.trim();
    if (!t) continue;
    if (/^p(\.|:|\))/i.test(t)) {
      if (q && a) items.push({ question: q, answer: a });
      q = t.replace(/^p(\.|:|\))\s*/i, "");
      a = "";
      continue;
    }
    if (/^r(\.|:|\))/i.test(t)) {
      a = t.replace(/^r(\.|:|\))\s*/i, "");
      continue;
    }
    if (t.includes("|")) {
      const [question, answer] = t.split("|").map((s) => s.trim());
      if (question && answer) items.push({ question, answer });
      continue;
    }
    if (!a) a = t;
    else q = t;
  }
  if (q && a) items.push({ question: q, answer: a });
  return items;
}

function formatFaqItems(items: { question: string; answer: string }[]): string {
  return items
    .filter((f) => f.question.trim() && f.answer.trim())
    .map((f) => `P: ${f.question}\nR: ${f.answer}`)
    .join("\n\n");
}

type FieldDef = {
  key: string;
  aba: string;
  promptBlock: string;
  label: string;
  listFormat?: ListFormat;
  /** Não entra em copiar, exportar nem template (produtos, links técnicos, etc.). */
  excludeFromUserFacing?: boolean;
  templateExample?: string;
  read: (s: AgentConfigSnapshot) => string;
  write: (s: AgentConfigSnapshot, value: string) => void;
};

type ScalarSpec = {
  key: keyof AgentConfigFormValues;
  aba: string;
  promptBlock: string;
  label: string;
  type?: "string" | "boolean" | "number";
  listFormat?: ListFormat;
  excludeFromUserFacing?: boolean;
  templateExample?: string;
};

const SCALAR_SPECS: ScalarSpec[] = [
  { key: "name", aba: ABA_PERFIL, promptBlock: "IDENTIDADE DO AGENTE", label: "Nome interno (equipe)", templateExample: "Atendimento comercial" },
  { key: "display_name", aba: ABA_PERFIL, promptBlock: "IDENTIDADE DO AGENTE", label: "Nome de apresentação", templateExample: "Ana" },
  { key: "greeting_message", aba: ABA_PERFIL, promptBlock: "IDENTIDADE DO AGENTE", label: "Mensagem de saudação", templateExample: "Olá! Sou a Ana, como posso ajudar você hoje?" },
  { key: "role", aba: ABA_PERFIL, promptBlock: "IDENTIDADE DO AGENTE", label: "Função do agente", templateExample: "Vendas" },
  { key: "tone", aba: ABA_PERFIL, promptBlock: "IDENTIDADE DO AGENTE", label: "Tom de voz", templateExample: "profissional e amigável" },
  { key: "language", aba: ABA_PERFIL, promptBlock: "IDENTIDADE DO AGENTE", label: "Idioma", templateExample: "pt-BR" },
  { key: "about_company", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO DO NEGÓCIO", label: "Sobre a empresa", templateExample: "Somos uma clínica de estética em São Paulo." },
  { key: "target_audience", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Público-alvo", templateExample: "Mulheres de 25 a 45 anos na região metropolitana" },
  { key: "value_proposition", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Diferenciais", templateExample: "Tratamentos personalizados com equipe especializada" },
  { key: "agent_goal", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Objetivo do agente", templateExample: "Qualificar o lead e agendar avaliação" },
  { key: "desired_next_step", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Próximo passo desejado", templateExample: "Agendar avaliação ou enviar orçamento" },
  { key: "service_area", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Área de atendimento", templateExample: "Grande São Paulo" },
  { key: "limitations", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Limites e restrições", templateExample: "Não prometer resultados em prazo fixo sem avaliação" },
  { key: "policies_summary", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Políticas importantes", templateExample: "Cancelamento com 24h de antecedência sem multa" },
  { key: "required_customer_data", aba: ABA_NEGOCIO, promptBlock: "CONTEXTO ESTRATÉGICO DO NEGÓCIO", label: "Dados a coletar do cliente", templateExample: "Nome, cidade e serviço desejado" },
  { key: "establishment_name", aba: ABA_NEGOCIO, promptBlock: "EMPRESA REPRESENTADA", label: "Nome do estabelecimento", templateExample: "Clínica Bella" },
  { key: "cnpj", aba: ABA_NEGOCIO, promptBlock: "EMPRESA REPRESENTADA", label: "CNPJ" },
  { key: "timezone", aba: ABA_NEGOCIO, promptBlock: "HORÁRIO DE FUNCIONAMENTO", label: "Fuso horário", templateExample: "America/Sao_Paulo" },
  { key: "restrict_to_business_hours", aba: ABA_NEGOCIO, promptBlock: "HORÁRIO DE FUNCIONAMENTO", label: "Restringir ao horário comercial", type: "boolean", templateExample: "não" },
  { key: "phone_numbers_enabled", aba: ABA_NEGOCIO, promptBlock: "TELEFONES DE CONTATO", label: "Incluir telefones no atendimento", type: "boolean", templateExample: "sim" },
  { key: "social_media_enabled", aba: ABA_NEGOCIO, promptBlock: "REDES SOCIAIS", label: "Incluir redes no atendimento", type: "boolean", templateExample: "sim" },
  { key: "comment_website_link_placement", aba: ABA_NEGOCIO, promptBlock: "COMENTÁRIO NO INSTAGRAM", label: "Onde enviar o site no comentário", templateExample: "No comentário público" },
  { key: "comment_product_link_placement", aba: ABA_NEGOCIO, promptBlock: "COMENTÁRIO NO INSTAGRAM", label: "Onde enviar o link de produto no comentário", templateExample: "Somente na DM" },
  { key: "comment_reply_mode", aba: ABA_NEGOCIO, promptBlock: "COMENTÁRIO NO INSTAGRAM", label: "Como responder comentários", templateExample: "Sempre comentário e DM" },
  { key: "other_social_media", aba: ABA_NEGOCIO, promptBlock: "REDES SOCIAIS", label: "Outras redes (texto livre)" },
  { key: "payment_installments", aba: ABA_NEGOCIO, promptBlock: "FORMAS DE PAGAMENTO", label: "Parcelamento", templateExample: "Até 12x sem juros" },
  { key: "show_prices_to_customer", aba: ABA_NEGOCIO, promptBlock: "REGRA DE PREÇO", label: "Pode mostrar preços (legado)", type: "boolean", templateExample: "sim" },
  { key: "price_disclosure_mode", aba: ABA_NEGOCIO, promptBlock: "REGRA DE PREÇO", label: "Quando falar de preços", templateExample: "Pode informar preços livremente" },
  { key: "price_disclosure_condition", aba: ABA_NEGOCIO, promptBlock: "REGRA DE PREÇO", label: "Condição extra para informar preço", templateExample: "Somente após o cliente informar o bairro" },
  { key: "delivery_enabled", aba: ABA_NEGOCIO, promptBlock: "INFORMAÇÕES DE ENTREGA", label: "Faz entrega", type: "boolean", templateExample: "não" },
  { key: "delivery_info", aba: ABA_NEGOCIO, promptBlock: "INFORMAÇÕES DE ENTREGA", label: "Detalhes da entrega", templateExample: "Entregamos em até 2 dias úteis na capital" },
  { key: "address_enabled", aba: ABA_NEGOCIO, promptBlock: "ENDEREÇOS", label: "Usar endereços no atendimento", type: "boolean", templateExample: "sim" },
  { key: "is_active", aba: ABA_COMPORTAMENTO, promptBlock: "PERMISSÕES E STATUS", label: "Agente ativo", type: "boolean", templateExample: "sim" },
  { key: "google_calendar_enabled", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Google Calendar habilitado", type: "boolean", templateExample: "sim" },
  { key: "google_calendar_sync_calendar_id", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Agenda Google (ID)", excludeFromUserFacing: true, templateExample: "" },
  { key: "appointment_duration", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Duração padrão (minutos)", type: "number", templateExample: "60" },
  { key: "max_appointment_options", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Máximo de horários a propor", type: "number", templateExample: "3" },
  { key: "can_schedule", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Pode agendar", type: "boolean", templateExample: "sim" },
  { key: "can_reschedule", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Pode remarcar", type: "boolean", templateExample: "sim" },
  { key: "can_cancel", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Pode cancelar", type: "boolean", templateExample: "sim" },
  { key: "scheduling_start_offset_days", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Antecedência mínima (dias)", type: "number", templateExample: "0" },
  { key: "scheduling_search_days", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Dias à frente para buscar horários", type: "number", templateExample: "5" },
  { key: "allow_overlapping_appointments", aba: ABA_COMPORTAMENTO, promptBlock: "AGENDAMENTO", label: "Permitir sobreposição de horários", type: "boolean", templateExample: "não" },
  { key: "llm_temperature", aba: ABA_COMPORTAMENTO, promptBlock: "TEMPERATURA DA IA", label: "Criatividade (0 = firme, 1 = solto)", type: "number", templateExample: "0.3" },
];

function buildFieldDefs(): FieldDef[] {
  const defs: FieldDef[] = [];

  for (const spec of SCALAR_SPECS) {
    const key = spec.key;
    const type = spec.type ?? "string";

    defs.push({
      key: String(key),
      aba: spec.aba,
      promptBlock: spec.promptBlock,
      label: spec.label,
      listFormat: spec.listFormat,
      excludeFromUserFacing: spec.excludeFromUserFacing,
      templateExample: spec.templateExample,
      read: (s) => {
        const v = s.form[key];
        if (type === "boolean") return boolToStr(Boolean(v));
        if (type === "number") return String(v ?? "");
        if (key === "role") return formatRole(String(v ?? ""));
        if (key === "price_disclosure_mode") return formatPriceMode(String(v ?? "free"));
        if (key === "comment_website_link_placement" || key === "comment_product_link_placement") {
          return formatLinkPlacement(String(v ?? ""));
        }
        if (key === "comment_reply_mode") return formatReplyMode(String(v ?? ""));
        return String(v ?? "");
      },
      write: (s, raw) => {
        if (type === "boolean") {
          (s.form as Record<string, unknown>)[key] = strToBool(raw, Boolean(s.form[key]));
        } else if (type === "number") {
          const n = Number(raw);
          (s.form as Record<string, unknown>)[key] = Number.isFinite(n) ? n : s.form[key];
        } else if (key === "role") {
          s.form.role = parseRole(raw, s.form.role);
        } else if (key === "price_disclosure_mode") {
          s.form.price_disclosure_mode = parsePriceMode(raw, s.form.price_disclosure_mode);
        } else if (key === "comment_website_link_placement") {
          s.form.comment_website_link_placement = parseLinkPlacement(raw, s.form.comment_website_link_placement);
        } else if (key === "comment_product_link_placement") {
          s.form.comment_product_link_placement = parseLinkPlacement(raw, s.form.comment_product_link_placement);
        } else if (key === "comment_reply_mode") {
          s.form.comment_reply_mode = parseReplyMode(raw, s.form.comment_reply_mode);
        } else {
          (s.form as Record<string, unknown>)[key] = raw;
        }
      },
    });
  }

  const listSpecs: FieldDef[] = [
    {
      key: "qualification_questions",
      aba: ABA_PERFIL,
      promptBlock: "PERGUNTAS DE QUALIFICAÇÃO",
      label: "Perguntas de qualificação (uma por linha)",
      listFormat: "numbered",
      templateExample: "1. Qual é o seu nome?\n2. Qual o melhor horário para contato?",
      read: (s) => formatStringList(s.qualificationQuestions),
      write: (s, raw) => {
        s.qualificationQuestions = parseStringList(raw);
      },
    },
    {
      key: "agent_instructions",
      aba: ABA_PERFIL,
      promptBlock: "INSTRUÇÕES DO AGENTE (PRIORIDADE MÁXIMA)",
      label: "Instruções do agente (uma por linha)",
      listFormat: "numbered",
      templateExample: "1. Sempre confirmar o nome antes de enviar proposta",
      read: (s) => formatStringList(s.agentInstructions),
      write: (s, raw) => {
        s.agentInstructions = parseStringList(raw);
      },
    },
    {
      key: "business_rules_list",
      aba: ABA_NEGOCIO,
      promptBlock: "REGRAS E POLÍTICAS DO NEGÓCIO — OBRIGATÓRIAS",
      label: "Regras do negócio (uma por linha)",
      listFormat: "numbered",
      templateExample: "1. Não inventar preços\n2. Confirmar cidade antes de agendar",
      read: (s) => formatStringList(s.businessRulesList),
      write: (s, raw) => {
        s.businessRulesList = parseStringList(raw);
      },
    },
    {
      key: "faq_items",
      aba: ABA_NEGOCIO,
      promptBlock: "PERGUNTAS FREQUENTES (FAQ) — RESPOSTAS OFICIAIS",
      label: "FAQ (P: pergunta / R: resposta)",
      listFormat: "faq",
      templateExample: "P: Qual o horário de funcionamento?\nR: Segunda a sexta, das 9h às 18h.",
      read: (s) => formatFaqItems(s.faqItems),
      write: (s, raw) => {
        s.faqItems = parseFaqItems(raw);
      },
    },
    {
      key: "business_category",
      aba: ABA_NEGOCIO,
      promptBlock: "SETOR DO NEGÓCIO",
      label: "Categoria do setor",
      templateExample: "other",
      read: (s) => s.businessCategory,
      write: (s, raw) => {
        s.businessCategory = raw.trim() || "other";
      },
    },
    {
      key: "business_tags",
      aba: ABA_NEGOCIO,
      promptBlock: "SETOR DO NEGÓCIO",
      label: "Tags do negócio (vírgula)",
      templateExample: "estética, facial, spa",
      read: (s) => s.businessTags.join(", "),
      write: (s, raw) => {
        s.businessTags = raw.split(",").map((x) => x.trim()).filter(Boolean);
      },
    },
    {
      key: "addresses",
      aba: ABA_NEGOCIO,
      promptBlock: "ENDEREÇOS",
      label: "Endereços (um por linha)",
      listFormat: "bullets",
      templateExample: "Av. Paulista, 1000 — São Paulo, SP",
      read: (s) => formatStringList(s.addresses.map((a) => a.address)),
      write: (s, raw) => {
        s.addresses = parseStringList(raw).map((address) => ({ address }));
      },
    },
    {
      key: "phone_numbers",
      aba: ABA_NEGOCIO,
      promptBlock: "TELEFONES DE CONTATO",
      label: "Telefones (um por linha)",
      listFormat: "bullets",
      templateExample: "5511999999999",
      read: (s) => formatStringList(s.form.phone_numbers ?? []),
      write: (s, raw) => {
        s.form.phone_numbers = parseStringList(raw);
      },
    },
    {
      key: "social_media",
      aba: ABA_NEGOCIO,
      promptBlock: "REDES SOCIAIS",
      label: "Redes e site (ex.: Instagram: @loja)",
      listFormat: "bullets",
      templateExample: "Instagram: @minhaloja\nWhatsApp: 5511999999999",
      read: (s) => formatSocialMedia(s.form.social_media),
      write: (s, raw) => {
        s.form.social_media = parseSocialMedia(raw, s.form.social_media);
      },
    },
    {
      key: "payment_methods",
      aba: ABA_NEGOCIO,
      promptBlock: "FORMAS DE PAGAMENTO",
      label: "Formas de pagamento (uma por linha)",
      listFormat: "bullets",
      templateExample: "Pix\nCartão de crédito",
      read: (s) => formatStringList(s.form.payment_methods ?? []),
      write: (s, raw) => {
        s.form.payment_methods = parseStringList(raw);
      },
    },
    {
      key: "human_handoff_enabled",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "TRANSFERÊNCIA PARA ATENDIMENTO HUMANO",
      label: "Transferência para humano habilitada",
      templateExample: "sim",
      read: (s) => boolToStr(s.humanHandoffEnabled),
      write: (s, raw) => {
        s.humanHandoffEnabled = strToBool(raw, s.humanHandoffEnabled);
      },
    },
    {
      key: "human_handoff_rules_v2",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "TRANSFERÊNCIA PARA ATENDIMENTO HUMANO",
      label: "Situações para passar ao humano (uma por linha)",
      listFormat: "numbered",
      templateExample: "1. Cliente pedir para falar com uma pessoa\n2. Reclamação grave ou urgente",
      read: (s) =>
        s.humanHandoffRulesV2.length > 0
          ? formatStringList(s.humanHandoffRulesV2.map((r) => r.condition))
          : formatStringList(s.humanHandoffRules),
      write: (s, raw) => {
        s.humanHandoffRulesV2 = parseHandoffRulesV2(raw);
      },
    },
    {
      key: "follow_up_enabled",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "FOLLOW-UP AUTOMÁTICO",
      label: "Follow-up automático",
      templateExample: "sim",
      read: (s) => boolToStr(s.followUpEnabled),
      write: (s, raw) => {
        s.followUpEnabled = strToBool(raw, s.followUpEnabled);
      },
    },
    {
      key: "follow_up_interval_minutes",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "FOLLOW-UP AUTOMÁTICO",
      label: "Intervalo entre tentativas (minutos)",
      templateExample: "60",
      read: (s) => String(s.followUpInterval),
      write: (s, raw) => {
        const n = Number(raw);
        if (Number.isFinite(n)) s.followUpInterval = n;
      },
    },
    {
      key: "follow_up_max_attempts",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "FOLLOW-UP AUTOMÁTICO",
      label: "Máximo de tentativas",
      templateExample: "3",
      read: (s) => String(s.followUpMaxAttempts),
      write: (s, raw) => {
        const n = Number(raw);
        if (Number.isFinite(n)) s.followUpMaxAttempts = n;
      },
    },
    {
      key: "catalog_file_enabled",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "CATÁLOGO / CARDÁPIO EM PDF",
      label: "Envia catálogo em PDF",
      templateExample: "não",
      read: (s) => boolToStr(s.catalogEnabled),
      write: (s, raw) => {
        s.catalogEnabled = strToBool(raw, s.catalogEnabled);
      },
    },
    {
      key: "catalog_trigger_phrases",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "CATÁLOGO / CARDÁPIO EM PDF",
      label: "Palavras-chave extras para enviar catálogo",
      templateExample: "tabela de preços; folder",
      read: (s) => formatStringList(s.catalogTriggerPhrases),
      write: (s, raw) => {
        s.catalogTriggerPhrases = parseStringList(raw);
      },
    },
    {
      key: "catalog_file_name",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "CATÁLOGO / CARDÁPIO EM PDF",
      label: "Nome do PDF do catálogo",
      templateExample: "catalogo.pdf",
      read: (s) => s.catalogFileName ?? "",
      write: (s, raw) => {
        s.catalogFileName = raw.trim() || null;
      },
    },
    {
      key: "human_handoff_rules",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "TRANSFERÊNCIA PARA ATENDIMENTO HUMANO",
      label: "Regras antigas de escalação",
      excludeFromUserFacing: true,
      read: (s) => formatStringList(s.humanHandoffRules),
      write: (s, raw) => {
        s.humanHandoffRules = parseStringList(raw);
      },
    },
    {
      key: "selected_product_ids",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "PRODUTOS",
      label: "IDs de produtos (técnico)",
      excludeFromUserFacing: true,
      read: (s) => s.selectedProductIds.join(", "),
      write: (s, raw) => {
        s.selectedProductIds = raw.split(",").map((x) => x.trim()).filter(Boolean);
      },
    },
    {
      key: "business_hours",
      aba: ABA_NEGOCIO,
      promptBlock: "HORÁRIO DE FUNCIONAMENTO",
      label: "Horários (configure na tela)",
      excludeFromUserFacing: true,
      read: (s) => formatStringList(
        s.businessHours.map((h) => {
          const brk = h.break_start && h.break_end ? ` (intervalo ${h.break_start}-${h.break_end})` : "";
          return `Dia ${h.day_of_week}: ${h.start_time}-${h.end_time}${brk}${h.is_enabled ? "" : " (fechado)"}`;
        }),
      ),
      write: (s, raw) => {
        const json = parseJson<DaySchedule[]>(raw, []);
        if (raw.trim().startsWith("[")) s.businessHours = json;
      },
    },
    {
      key: "catalog_file_url",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "CATÁLOGO / CARDÁPIO EM PDF",
      label: "Link do catálogo (técnico)",
      excludeFromUserFacing: true,
      read: (s) => s.catalogFileUrl ?? "",
      write: (s, raw) => {
        s.catalogFileUrl = raw.trim() || null;
      },
    },
    {
      key: "catalog_public_url",
      aba: ABA_COMPORTAMENTO,
      promptBlock: "CATÁLOGO / CARDÁPIO EM PDF",
      label: "Link público do catálogo (técnico)",
      excludeFromUserFacing: true,
      read: (s) => s.catalogPublicUrl ?? "",
      write: (s, raw) => {
        s.catalogPublicUrl = raw.trim() || null;
      },
    },
  ];

  defs.push(...listSpecs);

  const abaOrder = [ABA_PERFIL, ABA_NEGOCIO, ABA_COMPORTAMENTO];
  const abaIndex = new Map(abaOrder.map((a, i) => [a, i]));
  defs.sort((a, b) => {
    const abaDiff = (abaIndex.get(a.aba) ?? 99) - (abaIndex.get(b.aba) ?? 99);
    if (abaDiff !== 0) return abaDiff;
    return a.promptBlock.localeCompare(b.promptBlock, "pt-BR");
  });

  return defs;
}

const FIELD_DEFS = buildFieldDefs();
const KEY_BY_LABEL = new Map<string, string>();
for (const def of FIELD_DEFS) {
  KEY_BY_LABEL.set(normalizeHeader(def.label), def.key);
}

/** Labels antigos (exportações anteriores) continuam funcionando na importação. */
const LEGACY_LABELS: Record<string, string> = {
  "nome interno": "name",
  "nome de apresentacao": "display_name",
  "mensagem de saudacao": "greeting_message",
  "funcao (vendas|suporte|sdr|agendamentos)": "role",
  "perguntas de qualificacao (json ou uma por linha)": "qualification_questions",
  "regras de negocio (json ou uma por linha)": "business_rules_list",
  "regras de escalacao v2 (json)": "human_handoff_rules_v2",
  "telefones (json ou um por linha)": "phone_numbers",
  "redes sociais (json)": "social_media",
};

for (const [legacy, key] of Object.entries(LEGACY_LABELS)) {
  KEY_BY_LABEL.set(legacy, key);
}

const DEF_BY_KEY = new Map(FIELD_DEFS.map((f) => [f.key, f]));

function toExportRow(def: FieldDef, valor: string): AgentConfigRow {
  return {
    secao: def.aba,
    blocoPrompt: def.promptBlock,
    campo: def.label,
    valor,
    chave: def.key,
  };
}

/** Campos para copiar, exportar e template — sem produtos, horários técnicos nem URLs internas. */
function getUserFacingFieldDefs(): FieldDef[] {
  const seenKeys = new Set<string>();
  return FIELD_DEFS.filter((d) => {
    if (d.excludeFromUserFacing || seenKeys.has(d.key)) return false;
    seenKeys.add(d.key);
    return true;
  });
}

export function snapshotToRows(snapshot: AgentConfigSnapshot): AgentConfigRow[] {
  return getUserFacingFieldDefs().map((def) => toExportRow(def, def.read(snapshot)));
}

export function generateAgentConfigTemplateRows(): AgentConfigRow[] {
  const instructions = INSTRUCTION_ROWS;
  const fields = getUserFacingFieldDefs().map((def) =>
    toExportRow(def, def.templateExample ?? ""),
  );
  return [...instructions, ...fields];
}

export function generateAgentConfigTemplateCsv(): Blob {
  return exportRowsToCsv(generateAgentConfigTemplateRows());
}

export function generateAgentConfigTemplateXlsx(): Blob {
  const rows = generateAgentConfigTemplateRows();
  const configSheet = XLSX.utils.json_to_sheet(
    rows.map((r) => ({
      Aba: r.secao,
      "Bloco no prompt": r.blocoPrompt ?? "",
      "O que configurar": r.campo,
      "Sua resposta": r.valor,
    })),
  );
  configSheet["!cols"] = [{ wch: 14 }, { wch: 36 }, { wch: 40 }, { wch: 52 }];

  const helpSheet = XLSX.utils.aoa_to_sheet([
    ["Dica", "Detalhe"],
    ["Colunas", "Aba · Bloco no prompt · O que configurar · Sua resposta"],
    ["Abas", "Perfil · Negócio · Comportamento (igual à tela do agente)"],
    ["Regras", "Uma por linha; pode usar 1. 2. 3. — vira lista numerada no prompt"],
    ["FAQ", "P: pergunta e R: resposta em linhas separadas"],
    ["Importar", "Só linhas preenchidas alteram o agente; depois clique em Salvar"],
  ]);
  helpSheet["!cols"] = [{ wch: 16 }, { wch: 56 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, configSheet, "Preencher agente");
  XLSX.utils.book_append_sheet(wb, helpSheet, "Como usar");
  const data = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function formatRowsAsPlainText(rows: AgentConfigRow[], agentName?: string): string {
  const lines: string[] = [];
  lines.push(agentName ? `# Configurações do agente: ${agentName}` : "# Configurações do agente");
  lines.push("");
  lines.push(
    "> Espelho das abas **Perfil**, **Negócio** e **Comportamento**, na mesma ordem das seções do prompt da IA.",
  );
  lines.push("> Produtos vinculados e grade de horários não entram na cópia — configure na tela do agente.");
  lines.push("");

  const dataRows = rows.filter((r) => r.secao !== "Como usar");
  let currentAba = "";
  let currentBlock = "";

  for (const row of dataRows) {
    if (row.secao !== currentAba) {
      currentAba = row.secao;
      currentBlock = "";
      lines.push(`## Aba ${currentAba}`);
      lines.push("");
    }

    const block = row.blocoPrompt ?? "";
    if (block && block !== currentBlock) {
      currentBlock = block;
      lines.push(`### ${block}`);
      const hint = PROMPT_BLOCK_HINTS[block];
      if (hint) lines.push(`> ${hint}`);
      lines.push("");
    }

    const def = row.chave ? DEF_BY_KEY.get(row.chave) : undefined;
    lines.push(`**${row.campo}**`);
    lines.push(formatValueForCopy(row.valor, def?.listFormat));
    lines.push("");
  }

  return `${lines.join("\n").trimEnd()}\n`;
}

const EXPORT_HEADERS = ["Aba", "Bloco no prompt", "O que configurar", "Sua resposta"] as const;

export function exportRowsToCsv(rows: AgentConfigRow[]): Blob {
  const dataRows = rows.filter((r) => r.secao !== "Como usar" || r.valor.trim());
  const lines = [
    [...EXPORT_HEADERS],
    ...dataRows.map((r) => [r.secao, r.blocoPrompt ?? "", r.campo, r.valor]),
  ];
  const csv = lines
    .map((cols) =>
      cols
        .map((c) => {
          const s = String(c ?? "");
          if (s.includes('"') || s.includes(",") || s.includes("\n")) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(","),
    )
    .join("\n");
  return new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
}

export function exportRowsToXlsx(rows: AgentConfigRow[]): Blob {
  const dataRows = rows.filter((r) => r.secao !== "Como usar" || r.valor.trim());
  const sheet = XLSX.utils.json_to_sheet(
    dataRows.map((r) => ({
      Aba: r.secao,
      "Bloco no prompt": r.blocoPrompt ?? "",
      "O que configurar": r.campo,
      "Sua resposta": r.valor,
    })),
  );
  sheet["!cols"] = [{ wch: 14 }, { wch: 36 }, { wch: 40 }, { wch: 52 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Configurações");
  const data = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function resolveFieldKey(campoRaw: string, chaveRaw: string, secaoRaw?: string): string | null {
  if (normalizeHeader(secaoRaw ?? "") === "como usar") return null;

  const chave = chaveRaw.trim();
  if (chave && DEF_BY_KEY.has(chave)) return chave;

  const campo = normalizeHeader(campoRaw);
  if (KEY_BY_LABEL.has(campo)) return KEY_BY_LABEL.get(campo)!;
  if (DEF_BY_KEY.has(campo)) return campo;

  return null;
}

function pickColumnKey(keys: string[], candidates: string[], fallbackIdx?: number): string | undefined {
  const found = keys.find((k) => candidates.includes(normalizeHeader(k)));
  if (found) return found;
  if (fallbackIdx !== undefined && keys[fallbackIdx]) return keys[fallbackIdx];
  return undefined;
}

export async function parseAgentConfigFile(file: File): Promise<AgentConfigRow[]> {
  const isExcel = file.name.endsWith(".xlsx") || file.name.endsWith(".xls");

  if (isExcel) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName =
      workbook.SheetNames.find((n) => normalizeHeader(n).includes("preencher")) ?? workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
    return json
      .map((row) => {
        const keys = Object.keys(row);
        const secaoKey = pickColumnKey(keys, ["aba", "secao", "section", "grupo", "categoria"]);
        const blocoKey = pickColumnKey(keys, ["bloco no prompt", "bloco prompt", "bloco", "secao prompt"]);
        const campoKey = pickColumnKey(keys, [
          "o que configurar",
          "campo",
          "configuracao",
          "pergunta",
          "field",
          "nome",
        ], 0);
        const chaveKey = pickColumnKey(keys, ["chave", "key", "id"]);
        const valorKey = pickColumnKey(keys, [
          "sua resposta",
          "valor",
          "value",
          "resposta",
          "conteudo",
          "configuracao valor",
        ], keys.length - 1);
        return {
          secao: secaoKey ? String(row[secaoKey] ?? "") : "",
          blocoPrompt: blocoKey ? String(row[blocoKey] ?? "") : "",
          campo: campoKey ? String(row[campoKey] ?? "") : "",
          chave: chaveKey ? String(row[chaveKey] ?? "") : "",
          valor: valorKey ? String(row[valorKey] ?? "") : "",
        };
      })
      .filter((r) => r.campo.trim() || r.chave.trim());
  }

  const text = await file.text();
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];

  const delim = lines[0].includes(";") && !lines[0].includes(",") ? ";" : ",";
  const headerParts = lines[0].split(delim).map((h) => h.replace(/^"|"$/g, "").trim());
  const secaoIdx = headerParts.findIndex((h) =>
    ["aba", "secao", "section", "grupo"].includes(normalizeHeader(h)),
  );
  const blocoIdx = headerParts.findIndex((h) =>
    ["bloco no prompt", "bloco prompt", "bloco"].includes(normalizeHeader(h)),
  );
  const campoIdx = headerParts.findIndex((h) =>
    ["o que configurar", "campo", "configuracao", "pergunta", "field"].includes(normalizeHeader(h)),
  );
  const chaveIdx = headerParts.findIndex((h) => ["chave", "key"].includes(normalizeHeader(h)));
  const valorIdx = headerParts.findIndex((h) =>
    ["sua resposta", "valor", "value", "resposta"].includes(normalizeHeader(h)),
  );

  const parseLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else inQuotes = !inQuotes;
      } else if (ch === delim && !inQuotes) {
        out.push(cur);
        cur = "";
      } else cur += ch;
    }
    out.push(cur);
    return out.map((c) => c.replace(/^"|"$/g, "").trim());
  };

  const rows: AgentConfigRow[] = [];
  const start = campoIdx >= 0 || valorIdx >= 0 || secaoIdx >= 0 ? 1 : 0;

  for (let i = start; i < lines.length; i++) {
    const parts = parseLine(lines[i]);
    if (parts.length < 2) continue;
    rows.push({
      secao: secaoIdx >= 0 ? parts[secaoIdx] : "",
      blocoPrompt: blocoIdx >= 0 ? parts[blocoIdx] : "",
      campo: campoIdx >= 0 ? parts[campoIdx] : parts[0],
      chave: chaveIdx >= 0 ? parts[chaveIdx] : "",
      valor: valorIdx >= 0 ? parts[valorIdx] : parts[parts.length - 1],
    });
  }
  return rows;
}

export type ApplyImportResult = {
  snapshot: AgentConfigSnapshot;
  appliedKeys: string[];
  skipped: string[];
};

export function applyRowsToSnapshot(
  base: AgentConfigSnapshot,
  rows: AgentConfigRow[],
): ApplyImportResult {
  const snapshot = structuredClone(base);
  const appliedKeys: string[] = [];
  const skipped: string[] = [];

  for (const row of rows) {
    const key = resolveFieldKey(row.campo, row.chave ?? "", row.secao);
    if (!key) {
      if (row.campo.trim() || row.chave?.trim()) {
        skipped.push(row.campo || row.chave || "(linha vazia)");
      }
      continue;
    }
    if (!row.valor.trim()) {
      continue;
    }
    const def = DEF_BY_KEY.get(key)!;
    def.write(snapshot, row.valor);
    appliedKeys.push(key);
  }

  return { snapshot, appliedKeys, skipped };
}

export function getRoleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}
